import { Link } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { useProgress } from '@/hooks/useProgress';
import { TOTAL_PROBLEMS } from '@/data/problems';

export function Logo({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-2 font-bold tracking-tight ${className}`}>
      <span className="grid size-8 place-items-center rounded-md bg-brand font-mono text-sm font-extrabold text-brand-foreground shadow-sm">
        JS
      </span>
      <span>
        JS<span className="text-brand-strong">Bat</span>
      </span>
    </span>
  );
}

export function SiteLayout({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useTheme();
  const { progress } = useProgress();
  const solved = Object.values(progress).filter((p) => p.solved).length;
  const isDark = theme === 'dark';

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" className="rounded-md text-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Logo />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/progress">
                <span className="hidden sm:inline">Progress</span>
                <span className="rounded-full bg-brand px-2 py-0.5 font-mono text-xs font-semibold text-brand-foreground">
                  {solved}/{TOTAL_PROBLEMS}
                </span>
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/help">Help</Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? <Sun /> : <Moon />}
            </Button>
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-4 py-6 text-sm text-muted-foreground sm:flex-row sm:px-6">
          <p>
            JSBat — free JavaScript practice problems. Inspired by{' '}
            <a href="https://codingbat.com" className="underline underline-offset-4 hover:text-foreground" target="_blank" rel="noreferrer">
              CodingBat
            </a>
            .
          </p>
          <p>
            <a href="https://shakespeare.diy" className="underline underline-offset-4 hover:text-foreground" target="_blank" rel="noreferrer">
              Vibed with Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
